import { add } from 'date-fns';
import { head, last } from 'lodash';

import { MODE_WATER } from '@/lib/constants';
import { sumTotalCo2Emissions } from '@/lib/models/emissions/utils/count-emissions';
import { RouteBlocksInterface, RouteScheduleSegmentInterface, ScheduleRequestLineQueryInterface } from '@/types';
import { convertModeVerb, formatSegmentDescription } from '@/utils/format';
import { getUniqueSegmentLocationIds } from '@/utils/locations';
import { getRouteStopovers, getRouteTransfers, getTotalDuration, groupSegmentsAtTransfers } from '@/utils/route';

import { getPreferredIdentifier } from '../helpers';

/**
 * Merges Grouped Schedule Segments into Sequences
 * @param segments
 * @returns
 */
export const mergeScheduleSegmentGroups = (
  segments: RouteScheduleSegmentInterface[],
): RouteScheduleSegmentInterface[] => {
  const groups = groupSegmentsAtTransfers(segments);

  // Map through and merge grouped segments
  const mergedSegments = groups.map((segmentGroup) => {
    // early out if group has only 1 member
    if (segmentGroup.length === 1) return head(segmentGroup);

    // Else get first and last segment and merge them into 1
    const firstSegment = head(segmentGroup);
    const lastSegment = last(segmentGroup);
    // Get some details
    const { mode } = firstSegment || {};
    const segmentTransferType = firstSegment?.transferType || null;
    const duration = getTotalDuration(segmentGroup);
    const stopSegments = getRouteStopovers(segmentGroup);
    const stopIdentifiers = stopSegments.map((segment) => getPreferredIdentifier(segment.from?.identifiers));
    const stopIds = stopSegments.map((segment) => segment.from.nodeReference);
    const modePrefix = firstSegment ? convertModeVerb(firstSegment.mode, segmentTransferType) : '';
    const segmentEmissions = segmentGroup.map((segment) => segment.emissions);
    const totalEmissions = sumTotalCo2Emissions(segmentEmissions);

    // generate new description
    const description = formatSegmentDescription(
      firstSegment?.from,
      lastSegment?.to,
      modePrefix,
      mode,
      mode,
      segmentTransferType,
    );

    return {
      ...firstSegment,
      to: lastSegment?.to,
      fromTerminal: firstSegment?.fromTerminal || '',
      toTerminal: lastSegment?.toTerminal || '',
      arrivalDateTime: lastSegment?.arrivalDateTime,
      duration,
      description,
      stops: stopIdentifiers,
      stopIds,
      numStops: stopIdentifiers.length,
      emissions: totalEmissions,
    };
  });

  return mergedSegments.filter(Boolean) as RouteScheduleSegmentInterface[];
};

/**
 * Calculates how many timeline bars to display in schedules
 * @param timeRange - hours
 * @param rangeStartTime - date to start from
 * @returns - array of dates
 */
export const calculateScheduleTimelineBars = (
  timeRange: number,
  rangeStartTime: Date,
  isCompactView?: boolean,
): { bars: Date[]; timeRangeHours: number } => {
  const barWidth = calculateTimelineBarWidth(timeRange, isCompactView);
  const barCount = calculateTimelineBarCount(timeRange, barWidth);
  const timeRangeHours = barWidth * (barCount - 1);
  const bars = [...Array(barCount)].map((_, i) => add(rangeStartTime, { hours: barWidth * i }));

  return { bars, timeRangeHours };
};

/**
 * Calculates the width (in hours) of the bars to determine spacing
 */
const calculateTimelineBarWidth = (timeRange: number, isCompact?: boolean) => {
  if (timeRange > 96) return compactScaleOffset(Math.ceil(timeRange * 0.25), isCompact);
  if (timeRange > 72) return compactScaleOffset(48, isCompact);
  if (timeRange > 48) return compactScaleOffset(24, isCompact);
  if (timeRange > 36) return compactScaleOffset(12, isCompact);
  if (timeRange > 24) return compactScaleOffset(9, isCompact);
  if (timeRange > 12) return compactScaleOffset(6, isCompact);

  return compactScaleOffset(3, isCompact);
};

const compactScaleOffset = (value: number, isCompact?: boolean): number => (isCompact ? value * 2 : value);

/**
 * Handles calculating how many bars to draw
 * Takes into consideration if the UI is compact, and handles reducing the bar count
 */
const calculateTimelineBarCount = (timeRange: number, width: number) => {
  const barCount = timeRange / width;
  // Adds 1 to the count to ensure we always have 1 ending bar
  return Math.round(barCount) + 1;
};

/**
 * Generates a Line Query object for Air and Ocean Schedule requests
 * @param routeBlock RouteBlocksInterface
 * @returns ScheduleRequestLineQueryInterface[]
 */
export const generateScheduleLineQuery = (routeBlock: RouteBlocksInterface): ScheduleRequestLineQueryInterface[] => {
  // Remove any options that don't have primary carriers
  const scheduleAbleOptions = routeBlock.options.filter((option) => !!option.attributes.primaryCarriers.length);
  // Map and return Schedule Request Line Array
  return scheduleAbleOptions.map((option) => {
    // Get Transfers from Segments
    const transferLocations = getRouteTransfers(option.segments);

    // Ocean Properties
    if (option.mode === MODE_WATER) {
      return {
        nodes: getUniqueSegmentLocationIds(option.segments),
        transferAt: getUniqueSegmentLocationIds(transferLocations),
        carriers: option.attributes.primaryCarriers,
        multipleCarriers: false,
      };
    }

    // Air Properties
    return {
      via: getUniqueSegmentLocationIds(transferLocations),
      carriers: option.attributes.primaryCarriers,
    };
  });
};
